import React, { useState, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from '@/contexts/AuthContext';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Loader2, Check, X, Mail, RefreshCw, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import NewButton from "@/components/ui/NewButton";
import PremiumLoader from "@/components/ui/PremiumLoader";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { Checkbox } from "@/components/ui/checkbox";
import { TermsAndConditionsSheet } from "@/components/TermsAndConditionsSheet";
import { PrivacyPolicySheet } from "@/components/PrivacyPolicySheet";
import { supabase } from '@/integrations/supabase/client';
import { PasswordStrengthIndicator, validatePasswordStrength } from '@/components/auth/PasswordStrengthIndicator';
import logoLight from '@/components/logo/logo light.png';
import logoDark from '@/components/logo/logo dark.png';
import { useTheme } from 'next-themes';

const SignUp = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [usernameError, setUsernameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const { resolvedTheme } = useTheme();
  const { signUp, user, loading, checkUsernameAvailability } = useAuth();
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    const { valid } = validatePasswordStrength(password);
    if (!valid) {
      return "Password does not meet all requirements";
    }
    return "";
  };

  const validateUsername = async (usernameValue: string) => {
    if (!usernameValue.trim()) {
      setUsernameStatus('idle');
      setUsernameError('');
      return;
    }

    if (usernameValue.length < 3) {
      setUsernameStatus('taken');
      setUsernameError('Username must be at least 3 characters long');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(usernameValue)) {
      setUsernameStatus('taken');
      setUsernameError('Username can only contain letters, numbers, and underscores');
      return;
    }

    setUsernameStatus('checking');
    setUsernameError('');

    try {
      const result = await checkUsernameAvailability(usernameValue);
      if (result.available) {
        setUsernameStatus('available');
        setUsernameError('');
      } else {
        setUsernameStatus('taken');
        setUsernameError(result.error || 'Username already taken. Please choose another.');
      }
    } catch (error) {
      setUsernameStatus('taken');
      setUsernameError('Error checking username availability');
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUsername = e.target.value;
    setUsername(newUsername);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      validateUsername(newUsername);
    }, 500);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    const error = validatePassword(newPassword);
    setPasswordError(error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (usernameStatus === 'checking') {
      toast.error('Please wait for username validation to complete');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (passwordError) {
      return;
    }
    
    if (usernameStatus !== 'available') {
      setUsernameError('Please choose a valid, available username');
      return;
    }

    const result = await signUp(email, password, username, fullName);
    
    if (!result.success) {
      toast.error(result.error?.message || 'Failed to create account. Please try again.');
      return;
    }
    
    // Show email confirmation screen
    setIsEmailSent(true);
  };

  const handleResendEmail = async () => {
    try {
      setIsResending(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        toast.error(error.message || 'Failed to resend verification email');
      } else {
        toast.success('Verification email resent! Check your inbox.');
      }
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  const getUsernameStatusIcon = () => {
    switch (usernameStatus) {
      case 'checking':
        return <Loader2 className="h-4 w-4 animate-spin text-amber" />;
      case 'available':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'taken':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  // Email Sent Confirmation Screen
  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8">
          <Card className="bg-card dark:bg-zinc-800 backdrop-blur-sm border-border dark:border-zinc-700 shadow-xl">
            <CardContent className="pt-8 pb-6 px-6">
              <div className="text-center space-y-6">
                {/* Success Icon */}
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <Mail className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>

                {/* Title */}
                <div>
                  <h2 className="text-2xl font-bold text-foreground dark:text-white mb-2">
                    Verification Link Sent!
                  </h2>
                  <p className="text-muted-foreground">
                    We've sent a verification email to:
                  </p>
                  <p className="font-semibold text-foreground dark:text-white mt-1">
                    {email}
                  </p>
                </div>

                {/* Instructions */}
                <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-left">
                  <p className="text-amber-800 dark:text-amber-300 text-sm">
                    <strong>Important:</strong> Please check your <strong>Inbox</strong> and <strong>Spam/Junk folder</strong>.
                  </p>
                  <p className="text-amber-700 dark:text-amber-400 text-sm mt-2">
                    📧 <strong>Outlook/Hotmail users:</strong> Emails may take a few minutes to arrive due to their spam filtering.
                  </p>
                </div>

                {/* Resend Button */}
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Didn't receive the email?
                  </p>
                  <button
                    type="button"
                    onClick={handleResendEmail}
                    disabled={isResending}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-border dark:border-zinc-600 rounded-lg text-foreground dark:text-zinc-200 hover:bg-muted dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        <span>Resend Email</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Back to Login */}
                <div className="pt-4 border-t border-border dark:border-zinc-700">
                  <Link
                    to="/login"
                    className="text-amber hover:text-amber/80 font-semibold transition-colors duration-200"
                  >
                    ← Back to Sign In
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="space-y-4">
          {/* Back button + Logo row */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-medium border border-gray-300 bg-transparent transition-all hover:scale-105 active:scale-95 text-foreground"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <Link to="/" className="inline-block">
              <img src={resolvedTheme === 'dark' ? logoDark : logoLight} alt="Fork It" className="h-7 w-auto" />
            </Link>
            <div className="w-16" />
          </div>
          {/* Title + subtitle */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Join the Community
            </h2>
            <p className="text-muted-foreground">
              Discover and share the best dishes and products
            </p>
          </div>
        </div>

        {/* Sign Up Form */}
        <Card className="bg-card dark:bg-zinc-800 backdrop-blur-sm border-border dark:border-zinc-700 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl text-foreground dark:text-white text-center">
              Create Account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="username" className="text-base font-semibold text-foreground dark:text-zinc-200">
                  Username *
                </label>
                <div className="relative">
                  <Input
                    id="username"
                    type="text"
                    placeholder="Your unique @username"
                    value={username}
                    onChange={handleUsernameChange}
                    required
                    className="border-input dark:bg-zinc-700 dark:border-zinc-600 dark:text-white dark:placeholder:text-zinc-400 focus:border-lime focus:ring-2 focus:ring-lime/30 pr-10"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {getUsernameStatusIcon()}
                  </div>
                </div>
                {usernameError && (
                  <p className="text-sm text-red-500">{usernameError}</p>
                )}
                {usernameStatus === 'idle' && !username.trim() && (
                  <p className="text-sm text-muted-foreground">Please enter a username to continue</p>
                )}
                {usernameStatus === 'checking' && (
                  <p className="text-sm text-amber-600 dark:text-amber-400">Checking username availability...</p>
                )}
                {usernameStatus === 'available' && (
                  <p className="text-sm text-green-600 dark:text-green-400">✓ Username is available</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="fullName" className="text-base font-semibold text-foreground dark:text-zinc-200">
                  Full Name
                </label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="border-input dark:bg-zinc-700 dark:border-zinc-600 dark:text-white dark:placeholder:text-zinc-400 focus:border-lime focus:ring-2 focus:ring-lime/30"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="text-base font-semibold text-foreground dark:text-zinc-200">
                  Email *
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  className="border-input dark:bg-zinc-700 dark:border-zinc-600 dark:text-white dark:placeholder:text-zinc-400 focus:border-lime focus:ring-2 focus:ring-lime/30"
                />
                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}
                {!emailError && email && (
                  <p className="text-sm text-green-600 dark:text-green-400">✓ Valid email format</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-base font-semibold text-foreground dark:text-zinc-200">
                  Password *
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a strong password"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    className="border-input dark:bg-zinc-700 dark:border-zinc-600 dark:text-white dark:placeholder:text-zinc-400 focus:border-lime focus:ring-2 focus:ring-lime/30 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground dark:hover:text-white"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <PasswordStrengthIndicator password={password} />
              </div>

              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-snug cursor-pointer select-none">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="text-amber hover:underline font-medium"
                  >
                    Terms and Conditions
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={() => setShowPrivacy(true)}
                    className="text-amber hover:underline font-medium"
                  >
                    Privacy Policy
                  </button>
                </label>
              </div>

              <div className="flex justify-center pt-2">
                <NewButton
                  type="submit"
                  disabled={loading || usernameStatus !== 'available' || !!emailError || !!passwordError || !termsAccepted}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <PremiumLoader size="sm" text="" />
                      <span>Creating Account...</span>
                    </div>
                  ) : (
                    'Create Account'
                  )}
                </NewButton>
              </div>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border dark:border-zinc-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card dark:bg-zinc-800 text-muted-foreground">OR</span>
                </div>
              </div>

              {/* Google Auth */}
              <GoogleAuthButton />
            </form>
          </CardContent>
        </Card>

        {/* Sign In Link */}
        <div className="text-center">
          <p className="text-muted-foreground">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-amber hover:text-amber/80 font-semibold transition-colors duration-200"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>

      <TermsAndConditionsSheet isOpen={showTerms} onOpenChange={setShowTerms} />
      <PrivacyPolicySheet isOpen={showPrivacy} onOpenChange={setShowPrivacy} />
    </div>
  );
};

export default SignUp;
